import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import countryCodes from '../data/countryCodes';

const InquiryModal = ({ isOpen, onClose, buttonContext = '', packageTitle = 'Basic', packageAmount = 45, tmSignUpType = null }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', description: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && !detectedCountry) {
      (async () => {
        try {
          const info = await getCountryInfo();
          if (info) {
            setDetectedCountry(info);
            const match = countryCodes.find(c => c.country === info.countryCode);
            if (match) setCountryCode(match.code);
          }
        } catch (e) { console.error('Geo fetch error:', e); }
      })();
    }
  }, [isOpen, detectedCountry]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length === 10;

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9\s\-]/g, '');
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length > 10) return;
    setFormData({ ...formData, phone: value });
    if (errors.phone && digits.length === 10) setErrors(prev => ({ ...prev, phone: '' }));
  };

  const handleEmailChange = (e) => {
    setFormData({ ...formData, email: e.target.value });
    if (errors.email && validateEmail(e.target.value)) setErrors(prev => ({ ...prev, email: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email address'; hasErrors = true; }
    if (!validatePhone(formData.phone)) { newErrors.phone = 'Phone must be exactly 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); toast.error('Please fix the errors'); return; }

    setIsLoading(true);
    try {
      const leadData = {
        name: formData.name, email: formData.email, phone: formData.phone,
        countryCode, country: countryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: formData.description
      };
      const packageInfo = { title: packageTitle, amount: packageAmount };

      // DEBUG: Log tmSignUpType before submission
      console.log(`[DEBUG] Submitting lead with tmSignUpType: ${tmSignUpType} (${tmSignUpType === 1 ? 'Self Filing' : tmSignUpType === 2 ? 'Attorney-Led' : 'Not Set'})`);

      const result = await submitLeadWithGeoLocation(leadData, packageInfo, buttonContext, tmSignUpType);
      if (result.success) {
        toast.success(result.message || 'Your inquiry has been submitted successfully!');
      } else {
        toast.error(result.message || 'Failed to submit. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div data-testid="inquiry-modal" className="bg-white rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.2)] max-w-lg w-full my-2 sm:my-4 relative max-h-[95vh] overflow-y-auto">
        <button data-testid="modal-close-btn" onClick={onClose} className="absolute top-2 right-2 bg-slate-100 hover:bg-slate-200 rounded-md z-10" aria-label="Close modal">
          <X className="h-4 w-4 text-slate-600" />
        </button>

        <div className="p-3 sm:p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-[#044B3B] rounded-md flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-white" weight="bold" />
            </div>
            <div>
              <h3 className="text-base font-heading font-bold text-slate-900">Get Your Free Consultation</h3>
              <p className="text-[10px] text-slate-500">Expert IP attorneys ready to assist</p>
            </div>
          </div>

          {detectedCountry && (
            <div className="mb-3 flex items-center gap-1.5 text-[10px] text-slate-400">
              <span>Detected: {detectedCountry.city ? `${detectedCountry.city}, ` : ''}{detectedCountry.countryName}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-slate-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <Input data-testid="modal-name-input" type="text" placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                  className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md focus:border-[#044B3B]" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-700 mb-1">Email <span className="text-red-500">*</span></label>
                <Input data-testid="modal-email-input" type="email" placeholder="john@company.com" value={formData.email} onChange={handleEmailChange}
                  onBlur={() => { if (formData.email && !validateEmail(formData.email)) setErrors(prev => ({ ...prev, email: 'Please enter a valid email' })); else setErrors(prev => ({ ...prev, email: '' })); }}
                  className={`w-full h-8 px-2 text-xs border rounded-md focus:border-[#044B3B] ${errors.email ? 'border-red-500' : 'border-slate-200'}`} required />
              </div>
            </div>
            {errors.email && <p className="text-red-500 text-[10px] -mt-2">{errors.email}</p>}

            <div>
              <label className="block text-[10px] font-semibold text-slate-700 mb-1">Phone <span className="text-red-500">*</span></label>
              <div className="flex gap-2">
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger data-testid="modal-country-select" className="w-16 h-8 px-1 border border-slate-200 rounded-md bg-white text-[10px] font-medium text-slate-700 focus:border-[#044B3B]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {countryCodes.map((c, i) => (<SelectItem key={i} value={c.code}>{c.flag} {c.code}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Input data-testid="modal-phone-input" type="tel" placeholder="123-456-7890" value={formData.phone} onChange={handlePhoneChange}
                  onBlur={() => { const d = formData.phone.replace(/[^\d]/g, ''); if (formData.phone && d.length !== 10) setErrors(prev => ({ ...prev, phone: 'Phone must be 10 digits' })); else setErrors(prev => ({ ...prev, phone: '' })); }}
                  className={`flex-1 h-8 px-2 text-xs border rounded-md focus:border-[#044B3B] ${errors.phone ? 'border-red-500' : 'border-slate-200'}`} required />
              </div>
              {errors.phone && <p className="text-red-500 text-[10px] mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-700 mb-1">How can we help? <span className="text-red-500">*</span></label>
              <Textarea data-testid="modal-description-input" placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={2}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md resize-none focus:border-[#044B3B]" />
            </div>

            <Button data-testid="modal-submit-btn" type="submit" disabled={isLoading}
              className="w-full bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-xs h-9 rounded-md disabled:opacity-50 hover:scale-100">
              {isLoading ? 'Processing...' : 'Submit Inquiry'}
            </Button>
            <p className="text-center text-[10px] text-slate-400">100% Secure & Confidential</p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;
